import LegalLayout from "./LegalLayout";

const sections = [
  {
    heading: "Who we are",
    paragraphs: [
      "BuildOnce Studio is a sole proprietorship registered in Singapore (UEN: 53518159X), operated by Jophy Orenza. We provide digital tools, advisory services, and brand strategy resources for founders and independent consultants.",
      "Contact: info@buildoncestudio.com · buildoncestudio.com",
    ],
  },
  {
    heading: "What personal data we collect",
    paragraphs: [
      "We collect personal data only when you interact with our products and services. This includes:",
    ],
    bullets: [
      "**Name and email address**, collected when you purchase the Clarity Kit, request a copy of your Free Clarity Check reflection, or otherwise subscribe to communications from the studio.",
      "**Payment information**, processed directly by Stripe. BuildOnce Studio does not store your card details.",
      "**Free Clarity Check answers**, the five short responses you type into the Free Clarity Check. These are anonymous unless you choose to enter your email at the end to receive a copy.",
      "**Exercise responses**, the answers you enter inside the Clarity Kit.",
      "**AI chat interactions**, messages you send to the AI thinking partner inside the Clarity Kit.",
      "**Usage data**, pages visited, time spent, and general interaction patterns via anonymised analytics.",
      "**Device and browser information**, collected automatically for security and performance purposes.",
    ],
  },
  {
    heading: "How we use your personal data",
    paragraphs: [
      "We use your data for the following purposes:",
    ],
    bullets: [
      "To deliver the Clarity Kit and provide you access after purchase.",
      "To send transactional emails, including your purchase confirmation, access link, and your Free Clarity Check reflection when you request a copy.",
      "To send occasional written notes from the studio to those who have opted in via the Free Clarity Check or another BuildOnce Studio channel. You can unsubscribe from any of these at any time using the link in the email footer.",
      "To respond to inquiries sent to info@buildoncestudio.com.",
      "To improve the Clarity Kit based on anonymised, aggregated usage patterns.",
      "To comply with legal and regulatory obligations under Singapore law.",
    ],
    note: "We do not use your personal data for advertising purposes. We do not sell your data to any third party.",
  },
  {
    heading: "Third parties who process your data",
    bullets: [
      "**Stripe**. Payment processing. Card details go directly to Stripe and are never stored by BuildOnce Studio. Located in the United States (PCI-DSS compliant).",
      "**Anthropic (Claude)**. Powers the AI thinking partner. Your messages are processed to generate reflections. Located in the United States.",
      "**Supabase**. Database for purchase records and access tokens. Located in Singapore (ap-southeast-1).",
      "**Emergent**. Website hosting and application deployment. Global CDN.",
      "**Resend**. Transactional email delivery (purchase confirmation, access link, Free Clarity Check reflection) and the buildonce-newsletter subscriber list. Located in the United States.",
    ],
    subSections: [
      {
        heading: "Cross-border transfer notice",
        paragraphs: [
          "Some of our third-party service providers are located outside Singapore. Where data is transferred overseas, we ensure that such transfers are made to organisations that provide a standard of protection comparable to Singapore's PDPA, or we have obtained your consent for such transfer.",
        ],
      },
    ],
  },
  {
    heading: "Data retention",
    bullets: [
      "**Purchase records:** retained for 7 years in accordance with Singapore accounting requirements.",
      "**Exercise responses and AI chat interactions:** retained for 12 months from date of purchase, unless you request earlier deletion.",
      "**Email communications:** retained for 3 years.",
    ],
    note: "After the applicable retention period, your data is securely deleted or anonymised.",
  },
  {
    heading: "Your rights under the PDPA",
    paragraphs: [
      "As a data subject under Singapore's Personal Data Protection Act 2012 (PDPA), you have the following rights:",
    ],
    bullets: [
      "**Right of access:** You may request a copy of the personal data we hold about you.",
      "**Right of correction:** You may request that we correct inaccurate or incomplete data.",
      "**Right of withdrawal:** You may withdraw your consent to the collection, use, or disclosure of your personal data at any time.",
      "**Right to erasure:** You may request deletion of your personal data, subject to legal retention obligations.",
    ],
    note: "To exercise any of these rights, email us at info@buildoncestudio.com. We will respond within 10 business days.",
  },
  {
    heading: "Cookies and analytics",
    paragraphs: [
      "Our website may use cookies and anonymised analytics tools to understand how visitors interact with our pages. We do not use cookies for advertising or tracking purposes. You can disable cookies in your browser settings without affecting access to our products.",
    ],
  },
  {
    heading: "AI data processing notice",
    paragraphs: [
      "The Clarity Kit includes an AI thinking partner powered by Anthropic's Claude. When you use this feature:",
    ],
    bullets: [
      "Your messages are transmitted to Anthropic's API for processing.",
      "Anthropic processes your inputs to generate reflections.",
      "BuildOnce Studio does not sell or share your AI chat content with any party other than Anthropic for the purpose of generating your response.",
      "Anthropic's data handling is governed by their own Privacy Policy at anthropic.com/privacy.",
      "Your AI interactions may be retained by BuildOnce Studio for up to 12 months to improve the product, subject to your consent.",
    ],
    subSections: [
      {
        heading: "Consent to AI processing",
        paragraphs: [
          "By using the AI thinking partner inside the Clarity Kit, you consent to your inputs being transmitted to Anthropic for the purpose of generating reflections. If you do not consent, do not use the AI chat feature. The rest of the Clarity Kit remains fully functional without it.",
        ],
      },
    ],
  },
  {
    heading: "Data security",
    paragraphs: [
      "We take reasonable technical and organisational measures to protect your personal data, including:",
    ],
    bullets: [
      "All data transmission is encrypted via HTTPS/TLS.",
      "API keys and credentials are stored in secure environment variables and are never exposed in client-facing code.",
      "Database access is restricted to authorised systems only.",
      "Payment processing is handled entirely by Stripe (PCI-DSS Level 1 certified).",
    ],
    note: "No system is completely immune to security risks. In the event of a data breach that may cause significant harm, we will notify affected individuals and the Personal Data Protection Commission (PDPC) as required by law.",
  },
  {
    heading: "Updates to this policy",
    paragraphs: [
      "We may update this Privacy Policy from time to time. The effective date at the top of this document will reflect the most recent revision. Continued use of our products after an update constitutes acceptance of the revised policy.",
    ],
  },
  {
    heading: "Contact",
    paragraphs: [
      "BuildOnce Studio · Jophy Orenza · info@buildoncestudio.com · buildoncestudio.com · UEN: 53518159X · Singapore",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testId="privacy-policy-page"
      eyebrow="Legal · Privacy"
      title="Privacy Policy."
      intro="How BuildOnce Studio collects, uses, and protects your personal data."
      sections={sections}
    />
  );
}
